import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Megaphone, MapPin, Store, UserRound, Paperclip, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { fetchPublicAd, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import BackButton from "@/components/BackButton";
import { AdCounters } from "@/components/vendor/AdCounters";
import { OwnerAdDetails } from "@/components/vendor/OwnerAdDetails";

const kindOf = (a) => ((a.content_type || "").startsWith("image/") ? "image" : (a.content_type || "").startsWith("video/") ? "video" : "file");

function AdMedia({ attachments, onZoom }) {
  if (!attachments.length) return null;
  return (
    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ad-media">
      {attachments.map((a) => {
        const k = kindOf(a);
        if (k === "image") return (
          <button key={a.file_id} type="button" onClick={() => onZoom(a)} className="block rounded-2xl overflow-hidden bg-slate-100 border border-slate-100" data-testid={`ad-image-${a.file_id}`}>
            <img src={fileUrl(a.url)} alt={a.filename} className="w-full max-h-96 object-contain" loading="lazy" />
          </button>
        );
        if (k === "video") return (
          <video key={a.file_id} src={fileUrl(a.url)} controls playsInline preload="metadata" className="w-full max-h-96 rounded-2xl bg-black" data-testid={`ad-video-${a.file_id}`} />
        );
        return (
          <a key={a.file_id} href={fileUrl(a.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-2xl border border-slate-200 hover:border-emerald-400 px-3 py-3 text-sm text-slate-700" data-testid={`ad-file-${a.file_id}`}>
            <Paperclip className="w-4 h-4 flex-shrink-0" /><span className="truncate">{a.filename}</span>
          </a>
        );
      })}
    </div>
  );
}

function OwnerLink({ owner }) {
  if (!owner?.link) return null;
  const Icon = owner.kind === "professional" ? UserRound : Store;
  return (
    <Link to={owner.link} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50 active:bg-emerald-100 px-4 py-2 text-sm font-semibold" data-testid="ad-owner-link">
      <Icon className="w-4 h-4" /> {owner.kind === "professional" ? "View Profile" : "Visit Shop Profile"}{owner.name ? `: ${owner.name}` : ""}
    </Link>
  );
}

export default function AdDetail() {
  const { id } = useParams();
  const [ad, setAd] = useState(null);
  const [err, setErr] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    setAd(null); setErr(false);
    fetchPublicAd(id).then(setAd).catch((e) => setErr(e?.response?.status === 404 ? "gone" : "retry"));
  }, [id, tries]);

  if (err === "retry") return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="ad-load-error">
      <AlertCircle className="w-10 h-10 text-amber-400 mx-auto mb-2" />
      <div className="font-display text-xl font-bold">Advertisement load nahi ho paya</div>
      <p className="text-sm text-slate-500 mt-1">Internet check karke dobara koshish karein.</p>
      <button onClick={() => setTries((n) => n + 1)} className="mt-4 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold" data-testid="ad-retry-btn">Dobara try karein</button>
    </div>
  );
  if (err) return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="ad-unavailable">
      <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
      <div className="font-display text-xl font-bold">Yeh advertisement abhi uplabdh nahi hai</div>
      <p className="text-sm text-slate-500 mt-1">Ho sakta hai yeh khatam ho gaya ho ya hata diya gaya ho.</p>
      <Link to="/" className="inline-block mt-4 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold" data-testid="ad-unavailable-home">Home par jaayein</Link>
    </div>
  );
  if (!ad) return <div className="py-16 text-center text-slate-500 inline-flex items-center justify-center gap-2 w-full"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="ad-detail-page">
      <BackButton fallback="/notifications" testid="ad-back-btn" />
      {ad.owner_details && <div className="mt-3 text-xs font-semibold text-emerald-800" data-testid="ad-owner-preview-label">Customers ko aapka ad aisa dikhta hai:</div>}
      <div className="mt-3 bg-white border border-slate-100 rounded-3xl p-5 sm:p-7">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-100 text-amber-800 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5"><Megaphone className="w-3 h-3" /> Sponsored</span>
          {ad.status && <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-slate-100 text-slate-600" data-testid="ad-owner-status">{ad.status}{ad.is_live ? "" : " · not public"}</span>}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2 break-words" data-testid="ad-title">{ad.title || ad.preview?.name || "Advertisement"}</h1>
        {ad.owner?.name && <div className="text-sm text-slate-500 mt-1" data-testid="ad-owner-name">by {ad.owner.name}</div>}
        {ad.message && <p className="text-sm sm:text-base text-slate-700 mt-3 whitespace-pre-line break-words" data-testid="ad-message">{ad.message}</p>}
        {ad.counters && (
          <div className="mt-4" data-testid="ad-owner-performance">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Aapke ad ki performance · sirf aapko dikhti hai</div>
            <AdCounters counters={ad.counters} testid="ad-detail-counters" />
          </div>
        )}
        <AdMedia attachments={ad.attachments || []} onZoom={setZoom} />
        {ad.areas?.length > 0 && (
          <div className="mt-5 flex items-start gap-1.5 text-xs text-slate-500" data-testid="ad-areas">
            <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{ad.areas.map((a) => [a.pincode, a.area, a.city].filter(Boolean).join(" ")).join(" · ")}</span>
          </div>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          <OwnerLink owner={ad.owner} />
          {ad.preview?.link && ad.preview.link !== ad.owner?.link && (
            <Link to={ad.preview.link} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-700 hover:border-emerald-400 px-4 py-2 text-sm font-semibold" data-testid="ad-target-link">{ad.preview.name || "Open"} <ExternalLink className="w-3.5 h-3.5" /></Link>
          )}
        </div>
      </div>
      {ad.owner_details && <OwnerAdDetails d={ad.owner_details} title={ad.title} message={ad.message} areas={ad.areas} files={ad.attachments} />}
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black border-0 [&>button]:text-white [&>button]:bg-black/70 [&>button]:rounded-full [&>button]:p-1.5 [&>button]:opacity-100" data-testid="ad-image-zoom">
          <DialogTitle className="sr-only">{zoom?.filename}</DialogTitle>
          {zoom && <img src={fileUrl(zoom.url)} alt={zoom.filename} className="w-full max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
